/**
 * integrity-service HTTP client + pure response interpreters (TI-739b).
 *
 * Calls go through `apiFetch` so the `X-Session-Id` / `X-Request-Id` correlation headers
 * are attached (trace FE → integrity-service). The short-lived telemetry JWT is forwarded
 * as the bearer; integrity-service candidate-route validation of that token is a backend
 * follow-up (runbook M2-D1) — today it is feature-flag-gated.
 */

import { apiFetch } from "../livekit/correlation";
import { INTEGRITY_ENDPOINTS } from "../livekit/endpoints";

function authHeaders(jwt) {
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

export async function startSession({ candidateId, candidateName, blob, jwt }) {
  const fd = new FormData();
  fd.append("candidate_id", candidateId);
  fd.append("candidate_name", candidateName || candidateId);
  fd.append("file", blob, "reference.jpg");
  return apiFetch(INTEGRITY_ENDPOINTS.START, {
    method: "POST",
    headers: authHeaders(jwt),
    body: fd,
  });
}

export async function verifyFrame({ candidateId, blob, jwt }) {
  const fd = new FormData();
  fd.append("candidate_id", candidateId);
  fd.append("file", blob, "frame.jpg");
  return apiFetch(INTEGRITY_ENDPOINTS.VERIFY_FRAME, {
    method: "POST",
    headers: authHeaders(jwt),
    body: fd,
  });
}

/** Biometric-consent decision (BIPA/GDPR audit trail). Best-effort; never blocks UX. */
export async function recordConsent({ candidateId, granted, noticeVersion, jwt }) {
  return apiFetch(INTEGRITY_ENDPOINTS.CONSENT, {
    method: "POST",
    headers: { ...authHeaders(jwt), "Content-Type": "application/json" },
    body: JSON.stringify({
      candidate_id: candidateId,
      granted: !!granted,
      notice_version: noticeVersion,
    }),
  });
}

export async function terminateSession({ candidateId, reason, jwt }) {
  return apiFetch(INTEGRITY_ENDPOINTS.TERMINATE, {
    method: "POST",
    headers: { ...authHeaders(jwt), "Content-Type": "application/json" },
    body: JSON.stringify({ candidate_id: candidateId, reason: reason || "Manual termination" }),
  });
}

/** Post a batch of normalized on-device edge signals (TI-735). Best-effort; never blocks. */
export async function postEdgeSignals({ candidateId, signals, jwt }) {
  if (!Array.isArray(signals) || signals.length === 0) return null;
  return apiFetch(INTEGRITY_ENDPOINTS.EDGE_SIGNALS, {
    method: "POST",
    headers: { ...authHeaders(jwt), "Content-Type": "application/json" },
    body: JSON.stringify({ candidate_id: candidateId, signals }),
  });
}

/**
 * Pure: map an on-device edge detect() result to the normalized signal events the server
 * ingests. A clean frame yields none; only deviations are reported (cost + privacy).
 */
export function toEdgeSignalEvents(signal, timestamp) {
  if (!signal || !signal.available) return [];
  const ts = timestamp || new Date().toISOString();
  const events = [];
  if (signal.multiple) events.push({ type: "multiple_faces", timestamp: ts });
  else if (!signal.present) events.push({ type: "no_face", timestamp: ts });
  if (signal.lookingAway) events.push({ type: "looking_away", timestamp: ts });
  return events;
}

const VERIFY_WARNINGS = {
  multiple_faces: "Multiple faces detected",
  no_face: "Face not visible",
  side_face: "Please look at the camera",
  face_not_centered: "Center your face in the frame",
  too_dark: "Your camera is too dark",
  too_bright: "Your camera is too bright",
};

/** Pure: map a verify-frame response body to a UI monitor state. */
export function interpretVerifyResponse(body) {
  if (!body || typeof body !== "object") {
    return { level: "ok", message: null, terminate: false };
  }
  if (body.terminate) {
    return { level: "critical", message: body.reason || "Verification failed", terminate: true };
  }
  const status = String(body.status || "");
  const suggestions = Array.isArray(body.suggestions) ? body.suggestions : [];
  if (status in VERIFY_WARNINGS) {
    return { level: "warning", message: suggestions[0] || VERIFY_WARNINGS[status], terminate: false };
  }
  if (body.verified === false) {
    return { level: "warning", message: "Identity not confirmed", terminate: false };
  }
  return { level: "ok", message: null, terminate: false };
}

/** Pure: map an on-device edge signal to a UI nudge (no server call). */
export function interpretEdgeSignal(signal) {
  if (!signal || !signal.available) return null;
  if (signal.multiple) return { level: "warning", message: "Multiple faces detected" };
  if (!signal.present) return { level: "warning", message: "Please stay in frame" };
  if (signal.lookingAway) return { level: "warning", message: "Please look at the screen" };
  return null;
}
